import { DOMContextBuilder } from './context/DOMContextBuilder';
import { Context } from './context/Context';
import { TickEventManager } from './util';
import { Camera } from './camera';
import { SceneRenderer } from './renderer';
import { DeferredSceneRenderer } from './renderer/DeferredSceneRenderer';
import { CompositeSceneRenderer } from './renderer/CompositeSceneRenderer';
import { Scene } from './scene/Scene';
import { ShaderManager } from './shader/generated';
import { SceneFeatureRegistry, DayNightCycleFeature } from './scene/feature';
import { PhysicsFeature } from './physics';
import { GraphicsSettings } from './renderer/GraphicsSettings';
import { AudioEngine, AudioResolver, AudioSettings, RuntimeAudioResolver } from './audio';

export class Engine {
  private isActive: boolean = false;
  private _activeRenderer?: SceneRenderer;
  private _graphicsSettings?: Partial<GraphicsSettings>;
  private activeFrameBuffer: WebGLFramebuffer | null = null; // null means rendering to canvas

  private _tickEventManager: TickEventManager;
  private _contextBuilder: DOMContextBuilder;
  private _ctx: Context;
  private _activeScene?: Scene;
  private _shaderManager: ShaderManager;
  private _featureRegistry: SceneFeatureRegistry;
  /**
   * Audio is lazily constructed on first access. This keeps headless / test
   * callers (which never play audio and may run without `window`) from paying
   * the AudioContext-creation cost or hitting the missing-global error.
   */
  private _audio?: AudioEngine;
  private _audioResolver: AudioResolver = new RuntimeAudioResolver();
  private _audioSettings?: Partial<AudioSettings>;

  constructor(
    contextBuilder: DOMContextBuilder,
    public activeCamera: Camera,
  ) {
    this._contextBuilder = contextBuilder;
    this._ctx = contextBuilder.createContext(this);

    this._tickEventManager = new TickEventManager();
    this._activeScene = undefined;
    this._shaderManager = new ShaderManager(this.ctx);
    this._featureRegistry = new SceneFeatureRegistry();
    this.registerBuiltInFeatures();
  }

  private registerBuiltInFeatures(): void {
    this._featureRegistry.register(
      'day-night-cycle',
      (options) => new DayNightCycleFeature(options),
    );
    this._featureRegistry.register('physics', () => new PhysicsFeature());
  }

  public get ctx(): Context {
    return this._ctx;
  }

  public get activeScene(): Scene | undefined {
    return this._activeScene;
  }

  public get shaderManager(): ShaderManager {
    return this._shaderManager;
  }

  public get contextBuilder(): DOMContextBuilder {
    return this._contextBuilder;
  }

  public get tickEventManager(): TickEventManager {
    return this._tickEventManager;
  }

  public get featureRegistry(): SceneFeatureRegistry {
    return this._featureRegistry;
  }

  public get renderer(): SceneRenderer | undefined {
    return this._activeRenderer;
  }

  public get graphicsSettings(): Readonly<GraphicsSettings> | undefined {
    return this.getDeferredRenderer()?.graphicsSettings;
  }

  public set graphicsSettings(settings: Partial<GraphicsSettings>) {
    this._graphicsSettings = settings;
    this.getDeferredRenderer()?.applySettings(settings);
  }

  /**
   * Lazily constructs the AudioEngine on first access. Apps that need to
   * customize the AudioResolver or initial settings should call
   * {@link configureAudio} before the first access.
   */
  public get audio(): AudioEngine {
    if (!this._audio) {
      this._audio = new AudioEngine({
        resolver: this._audioResolver,
        settings: this._audioSettings,
      });
    }
    return this._audio;
  }

  /**
   * Override the audio resolver and/or initial settings. Must be called before
   * any access to `engine.audio`; throws otherwise.
   */
  public configureAudio(opts: { resolver?: AudioResolver; settings?: Partial<AudioSettings> }): void {
    if (this._audio) {
      throw new Error('configureAudio() must be called before engine.audio is first accessed');
    }
    if (opts.resolver) this._audioResolver = opts.resolver;
    if (opts.settings) this._audioSettings = opts.settings;
  }

  public getDeferredRenderer(): DeferredSceneRenderer | undefined {
    if (this._activeRenderer instanceof CompositeSceneRenderer) {
      return this._activeRenderer.deferredRenderer;
    }
    if (this._activeRenderer instanceof DeferredSceneRenderer) {
      return this._activeRenderer;
    }
    return undefined;
  }

  public resize(): void {
    this._activeRenderer?.resize();
  }

  public deactivateScene(): void {
    if (this._activeScene) {
      // Audio is scene-scoped; stop everything before tearing down GL/Scene state.
      this._audio?.stopAll();
      this._activeRenderer?.destroy();
      this._activeScene.detached();
      this._activeRenderer = undefined;
      this._activeScene = undefined;
    }
  }

  public activateScene(scene: Scene) {
    if (!scene) throw new Error('scene must be set');

    this.deactivateScene();

    this.activeFrameBuffer = null; // Note: rendering to null means rendering to canvas
    this._activeScene = scene;
    this._activeRenderer = scene.createRenderer();
    this._activeRenderer.init(this.ctx);

    // Apply saved graphics settings after init so all settings take effect
    // (render scale, shadow resolutions, SSAO/god-rays divisors, LOD, etc.)
    if (this._graphicsSettings) {
      this.getDeferredRenderer()?.applySettings(this._graphicsSettings);
    }

    this._ctx.resetState();
    scene.attached();
  }

  public stop() {
    this.isActive = false;
  }

  public start() {
    this.isActive = true;
    this.renderLoop();
  }

  /**
   * Tear down OS-level resources owned by the engine. Stops the render loop,
   * deactivates the scene, and closes the AudioContext. Browsers cap concurrent
   * AudioContexts (~6 on Chrome), so failing to close on Angular hot-reload or
   * route teardown will silently exhaust the budget after a few reloads.
   */
  public destroy(): void {
    this.stop();
    this.deactivateScene();
    this._audio?.destroy();
    this._audio = undefined;
  }

  private renderLoop() {
    if (!this.isActive) {
      return;
    }

    const engineTickStart = performance.now();
    this.tickEventManager.tick();
    const engineTickMs = performance.now() - engineTickStart;

    let sceneTickMs = 0;
    if (this._activeScene && this._activeRenderer) {
      const sceneTickStart = performance.now();
      this._activeScene.tickEventManager.tick();
      sceneTickMs = performance.now() - sceneTickStart;
    }

    if (this._activeScene && this._activeRenderer) {
      const deferred = this.getDeferredRenderer();
      if (deferred) {
        const stats = deferred.renderStats;
        stats.tickMs = engineTickMs + sceneTickMs;
        const b = this._activeScene.lastTickBreakdown;
        const measuredScene =
          b.lightAnimMs +
          b.objAnimMs +
          b.featuresMs +
          b.decalsMs +
          b.physicsMs +
          b.terrainStreamMs +
          b.objectStreamMs;
        stats.tickBreakdown.engine = engineTickMs;
        stats.tickBreakdown.lightAnim = b.lightAnimMs;
        stats.tickBreakdown.objAnim = b.objAnimMs;
        stats.tickBreakdown.features = b.featuresMs;
        stats.tickBreakdown.decals = b.decalsMs;
        stats.tickBreakdown.physics = b.physicsMs;
        stats.tickBreakdown.terrainStream = b.terrainStreamMs;
        stats.tickBreakdown.objectStream = b.objectStreamMs;
        stats.tickBreakdown.sceneOther = Math.max(0, sceneTickMs - measuredScene);
        stats.streaming.terrainInstalls = b.terrainStreamInstalls;
        stats.streaming.terrainInstallMs = b.terrainStreamInstallMs;
        stats.streaming.terrainMaxInstallMs = b.terrainStreamMaxInstallMs;
        stats.streaming.terrainEvicts = b.terrainStreamEvicts;
        stats.streaming.terrainEvictMs = b.terrainStreamEvictMs;
        stats.streaming.terrainMaxEvictMs = b.terrainStreamMaxEvictMs;
      }
      this.renderFrame(this._activeRenderer, this.activeCamera);
    }

    requestAnimationFrame(() => this.renderLoop());
  }

  public renderFrame(renderer: SceneRenderer, camera: Camera) {
    if (!this._activeScene) {
      throw new Error(`No active scene has been set`);
    }

    camera.prepareFrame();

    // Audio update piggybacks on the render-frame call so the listener tracks
    // the same matrix the renderer is about to use. dt is in seconds; for now
    // pass 0 since fade scheduling uses ctx.currentTime directly.
    this._audio?.update(camera, 0);

    const scene = this._activeScene;
    const deferred = this.getDeferredRenderer();
    const timer = deferred?.timer;
    // One disjoint check per frame, before any timer queries are issued. The
    // shadow pass runs first, so this lives here rather than inside the
    // deferred renderer's render() entry.
    timer?.beginFrame();

    const shadowCpuStart = performance.now();
    timer?.begin('shadow');
    scene.lightManager.renderShadowMaps(this._ctx, scene, camera.frustum);
    timer?.end();
    const shadowCpuMs = performance.now() - shadowCpuStart;

    renderer.render(scene, camera, this.activeFrameBuffer);

    // Close out the frame-wide GPU query (no-op on per-pass frames). Must run
    // after renderer.render so it covers every GPU command in the frame.
    timer?.endFrame();

    if (deferred) {
      deferred.renderStats.shadowCpuMs = shadowCpuMs;
      deferred.renderStats.shadowGpuMs = timer?.getLastResultMs('shadow') ?? 0;
      deferred.renderStats.frameGpuMs = timer?.getLastResultMs('frame') ?? 0;
      deferred.renderStats.shadowRan = true;
      const sb = deferred.renderStats.shadowBreakdown;
      const ss = scene.lightManager.shadowStats;
      sb.lightLoopMs = ss.lightLoopMs;
      sb.getNonStaticMs = ss.getNonStaticMs;
      sb.renderStaticMs = ss.renderStaticMs;
      sb.renderDynamicMs = ss.renderDynamicMs;
      sb.dirtyCasters = ss.dirtyCasters;
      sb.shadowMapsRendered = ss.shadowMapsRendered;
    }
  }
}
